// ── SUMMER WORKSHOPS: admin catalog + roster ─────────────────────────
// Catalog CRUD (list/tabs/modal/save/duplicate/archive) is shared with
// Classes via catalog-shared.js. The Roster tab is NOT shared — Workshops'
// promote charges immediately (Promote & charge), unlike Classes' (see
// admin-tapvoice.js for that side).
//
// View-only admins reach both tabs (they need to see who's signed up), so
// every mutating control here -- "+ Add workshop" and "Promote & charge" --
// is canEdit(session)-gated; the roster GET itself is Middleware::requireAdmin,
// which view-only already satisfies.
//
// Catalog buttons wired via VIEW_INIT['a-workshops-catalog'] + catalog-
// shared.js's wireCatalogGroup('summer'). Roster's program-switch and
// promote buttons are wired by wireWorkshopsRoster(), called both from
// VIEW_INIT['a-workshops-roster'] and directly inside wrSelectProgram()
// itself — that function bypasses route()/VIEW_INIT by writing #main's
// innerHTML directly (see its own comment for why), so its own re-renders
// need their own explicit re-wire call too.
import { apiFetch } from '../core/api.js';
import { PROGRAMS, currentView, session } from '../core/state.js';
import { canEdit } from '../core/domain-utils.js';
import { toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT } from '../core/routing.js';
import { archivedPill, filterArchived, archivedShowSelect } from './admin-archive.js';
import { renderCatalogTabs, renderCatalogList, wireCatalogGroup, catalogModal } from './catalog-shared.js';

// Unlike the Store/Classes previews, Summer Workshops registration lives on
// its own public, unauthenticated page (catalog.html, at /summer-workshops.html) with a full
// cart/checkout flow already separate from portal.html -- anyone can already
// reach it directly. So "Preview" just opens the real page in a new tab
// rather than re-simulating its cart against fixture performers.
function previewWorkshops() {
  window.open('/summer-workshops.html', '_blank');
}
VIEWS['a-workshops-catalog'] = () => `
  <div class="page-head">
    <h2 class="serif">Summer <em>Workshops</em></h2>
  </div>
  <div class="card">
    <div class="card-hd">Catalog <span style="display:flex;gap:8px"><button class="btn-ghost btn-sm" data-action="preview-workshops">Preview</button>${canEdit(session) ? '<button class="btn-ghost btn-sm" data-action="add-workshop">+ Add workshop</button>' : ''}</span></div>
    ${renderCatalogTabs('summer')}
    <div id="workshopCatalogList">${renderCatalogList('summer')}</div>
  </div>`;

VIEW_INIT['a-workshops-catalog'] = () => {
  document.querySelectorAll('[data-action="preview-workshops"]').forEach(btn => {
    btn.addEventListener('click', previewWorkshops);
  });
  document.querySelectorAll('[data-action="add-workshop"]').forEach(btn => {
    btn.addEventListener('click', () => catalogModal('summer'));
  });
  wireCatalogGroup('summer');
};

// ── SUMMER WORKSHOPS: admin roster ────────────────────────────────────
let wrProgramId = null;
let wrRoster = null;    // null = not loaded yet for wrProgramId
let wrLoadingId = null; // id currently being fetched, if any
let wrShow = 'current';  // 'current' | 'archived' | 'all' -- see filterArchived()

// wrSelectProgram() and the view render function both call each other by
// design (the render function auto-kicks off a fetch on first view; the
// fetch re-renders when it resolves) -- wrLoadingId is what stops that from
// being mutually re-entrant. Without it, wrSelectProgram sets wrRoster=null
// and renders "loading", which re-triggers the render's own "wrRoster is
// null, kick off a fetch" check before the first fetch ever gets a chance to
// resolve, recursing without bound (confirmed: one render fired thousands of
// overlapping requests before the call stack gave out).
async function wrSelectProgram(id) {
  wrProgramId = id;
  wrRoster = null;
  wrLoadingId = id;
  if (currentView === 'a-workshops-roster') { document.getElementById('main').innerHTML = VIEWS['a-workshops-roster'](); wireWorkshopsRoster(); }
  try {
    const data = await apiFetch('/workshops/' + id + '/roster');
    if (wrProgramId !== id) return; // user switched programs while this was in flight
    wrRoster = data;
  } catch (e) {
    if (wrProgramId !== id) return;
    wrRoster = [];
    toastError(e.message);
  }
  wrLoadingId = null;
  if (currentView === 'a-workshops-roster') { document.getElementById('main').innerHTML = VIEWS['a-workshops-roster'](); wireWorkshopsRoster(); }
}
function wireWorkshopsRoster() {
  document.querySelectorAll('[data-action="wr-select-program"]').forEach(btn => {
    btn.addEventListener('click', () => wrSelectProgram(btn.dataset.program));
  });
  const showSel = document.getElementById('wrShowSelect');
  if (showSel) showSel.addEventListener('change', () => {
    wrShow = showSel.value;
    document.getElementById('main').innerHTML = VIEWS['a-workshops-roster'](); wireWorkshopsRoster();
  });
  document.querySelectorAll('[data-action="wr-promote"]').forEach(btn => {
    btn.addEventListener('click', () => promoteWorkshopEnrollment(btn.dataset.enrollment));
  });
}
VIEW_INIT['a-workshops-roster'] = wireWorkshopsRoster;
async function promoteWorkshopEnrollment(enrollmentId) {
  try {
    const result = await apiFetch('/workshops/' + enrollmentId + '/promote', { method: 'POST', body: JSON.stringify({}) });
    toast(result.message || 'Performer promoted.');
    await wrSelectProgram(wrProgramId); // re-fetch and re-render this workshop's roster
  } catch (e) { toastError(e.message); }
}
VIEWS['a-workshops-roster'] = () => {
  const workshops = PROGRAMS.filter(p => p.billingType === 'workshop' && p.workshopGroup === 'summer' && p.active !== false);
  if (!workshops.length) {
    return `<div class="page-head"><h2 class="serif">Workshop <em>Roster</em></h2></div>
      <p class="empty">No active workshops — check the Catalog tab's Archived list if you're looking for a past one.</p>`;
  }
  if (!wrProgramId || !workshops.some(p => p.id === wrProgramId)) wrProgramId = workshops[0].id;
  if (wrRoster === null && wrLoadingId !== wrProgramId) wrSelectProgram(wrProgramId); // kicks off the fetch; re-renders #main when it resolves

  const current = workshops.find(p => p.id === wrProgramId);
  const all = wrRoster || [];
  const rows = filterArchived(all, wrShow);
  const archivedCount = all.filter(r => r.archivedAt).length;
  return `
    <div class="page-head"><h2 class="serif">Workshop <em>Roster</em></h2></div>
    <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px">
      ${workshops.map(p => `<button class="btn-ghost btn-sm" style="${p.id === wrProgramId ? 'border-color:rgba(201,168,76,0.5);color:var(--gold-light)' : ''}" data-action="wr-select-program" data-program="${p.id}">${esc(p.name)}</button>`).join('')}
    </div>
    <div class="card">
      <div class="card-hd" style="display:flex;align-items:center;gap:10px">${esc(current ? current.name : '')} — ${rows.length} ${rows.length === 1 ? 'signup' : 'signups'}${archivedShowSelect('wrShowSelect', wrShow, archivedCount)}</div>
      ${wrRoster === null ? '<p class="empty">Loading…</p>' : !rows.length ? `<p class="empty">${all.length ? 'No ' + (wrShow === 'archived' ? 'archived' : 'current') + ' performers on this roster.' : 'No registrations yet.'}</p>` : `
      <div class="tbl-wrap"><table>
        <thead><tr><th>Performer</th><th>Age / Gender</th><th>Parent</th><th>Contact</th><th>Status</th><th>Payment</th><th></th></tr></thead>
        <tbody>
          ${rows.map(r => `<tr>
            <td>${r.accountId ? `<button class="tbl-link" onclick="viewParent('${r.accountId}')">${esc(r.performerName)}</button>` : esc(r.performerName)}${r.archivedAt ? ' ' + archivedPill() : ''}</td>
            <td style="color:var(--muted)">${r.age} · ${r.gender ? esc(r.gender) : '—'}</td>
            <td>${esc(r.parentName)}</td>
            <td style="color:var(--muted)">${esc(r.parentEmail)}${r.parentPhone ? '<br>' + esc(r.parentPhone) : ''}</td>
            <td>${r.status === 'waitlisted' ? '<span class="pill pill-off">waitlisted</span>' : '<span class="pill pill-off">registered</span>'}</td>
            <td>${r.status === 'waitlisted' ? '<span style="color:var(--muted)">—</span>' : (r.paymentStatus === 'paid' ? 'Paid' : esc(r.paymentStatus))}</td>
            <td style="text-align:right;white-space:nowrap">${r.status === 'waitlisted' && canEdit(session) ? `<button class="btn-ghost btn-sm" data-action="wr-promote" data-enrollment="${r.enrollmentId}">Promote &amp; charge</button>` : ''}</td>
          </tr>`).join('')}
        </tbody>
      </table></div>`}
    </div>`;
};
